import React from 'react';
import { motion } from 'framer-motion';
import { Flame, Sparkles } from 'lucide-react';

interface StreakFlameProps {
  streak: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export default function StreakFlame({ streak, size = 'md', showLabel = true }: StreakFlameProps) {
  const sizeClasses = {
    sm: { container: 'w-12 h-12', icon: 'w-6 h-6', text: 'text-sm' },
    md: { container: 'w-16 h-16', icon: 'w-8 h-8', text: 'text-lg' },
    lg: { container: 'w-24 h-24', icon: 'w-12 h-12', text: 'text-2xl' },
  };

  const classes = sizeClasses[size];
  
  // Flame intensity based on streak
  const intensity = Math.min(streak / 30, 1); // Max intensity at 30 days
  const flameColors = [
    'from-secondary to-secondary/80',           // Base - brand gold
    'from-secondary/90 to-accent/80',           // 7 days
    'from-accent to-secondary/90',              // 14 days
    'from-accent/90 to-secondary',              // 21 days
    'from-secondary via-accent to-secondary',   // 30+ days
  ];
  
  const getFlameColor = () => {
    if (streak >= 30) return flameColors[4];
    if (streak >= 21) return flameColors[3];
    if (streak >= 14) return flameColors[2];
    if (streak >= 7) return flameColors[1];
    return flameColors[0];
  };

  if (streak === 0) {
    return (
      <div className="flex flex-col items-center gap-1 opacity-50">
        <div className={`${classes.container} rounded-full bg-muted/30 flex items-center justify-center`}>
          <Flame className={`${classes.icon} text-muted-foreground`} />
        </div>
        {showLabel && (
          <span className={`${classes.text} font-bold text-muted-foreground`}>No streak</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative">
        {/* Glow effect */}
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ 
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
          className={`absolute inset-0 ${classes.container} rounded-full bg-gradient-to-t ${getFlameColor()} blur-xl`}
        />
        
        {/* Main flame container */}
        <motion.div
          animate={{ 
            y: [0, -3, 0],
          }}
          transition={{ 
            duration: 0.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
          className={`relative ${classes.container} rounded-full bg-gradient-to-t ${getFlameColor()} flex items-center justify-center shadow-lg`}
        >
          {/* Flame icon with flicker */}
          <motion.div
            animate={{ 
              scale: [1, 1.1, 0.95, 1.05, 1],
              rotate: [-2, 2, -1, 1, 0]
            }}
            transition={{ 
              duration: 0.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          >
            <Flame className={`${classes.icon} text-white drop-shadow-lg`} />
          </motion.div>

          {/* Sparkles for high streaks */}
          {streak >= 7 && (
            <>
              <motion.div
                animate={{ 
                  y: [-20, -40],
                  opacity: [1, 0],
                  scale: [0.5, 1]
                }}
                transition={{ 
                  duration: 1,
                  repeat: Infinity,
                  repeatDelay: 0.5
                }}
                className="absolute -top-2 left-1/4"
              >
                <Sparkles className="w-3 h-3 text-secondary" />
              </motion.div>
              <motion.div
                animate={{ 
                  y: [-20, -35],
                  opacity: [1, 0],
                  scale: [0.5, 1]
                }}
                transition={{ 
                  duration: 0.8,
                  repeat: Infinity,
                  repeatDelay: 0.7,
                  delay: 0.3
                }}
                className="absolute -top-2 right-1/4"
              >
                <Sparkles className="w-2 h-2 text-accent" />
              </motion.div>
            </>
          )}
        </motion.div>

        {/* Streak number badge */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-background border-2 border-primary flex items-center justify-center"
        >
          <span className="text-xs font-bold text-primary">{streak}</span>
        </motion.div>
      </div>

      {showLabel && (
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className={`${classes.text} font-bold text-foreground`}>
            {streak} Day{streak !== 1 ? 's' : ''}
          </span>
          {streak >= 30 && (
            <motion.p 
              className="text-xs text-primary"
              animate={{ opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              🔥 On Fire!
            </motion.p>
          )}
          {streak >= 7 && streak < 30 && (
            <p className="text-xs text-muted-foreground">Keep it burning!</p>
          )}
        </motion.div>
      )}
    </div>
  );
}
